import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { toast } from '../../utils/customToast';
import PricingModal from './PricingModal';
import TenantNameConflictModal from './TenantNameConflictModal';
import Modal from '../shared/Modal';

const CreateTenantModal = ({ onClose, onSuccess }) => {
    const [name, setName] = useState('');
    const [type, setType] = useState('professional');
    const [loading, setLoading] = useState(false);
    const { getAuthHeaders, switchTenant, user } = useAuth();
    const navigate = useNavigate();
    const [showPlanModal, setShowPlanModal] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState('free');
    const [showConflictModal, setShowConflictModal] = useState(false);
    const [conflictingName, setConflictingName] = useState('');

    const nameInputRef = useRef(null);
    const cancelRef = useRef(null);
    const confirmRef = useRef(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Vérifier si l'email est vérifié
        if (!user?.isEmailVerified) {
            toast.error('Veuillez d\'abord vérifier votre adresse email pour créer une organisation');
            return;
        }
        
        // open plan picker
        setShowPlanModal(true);
    };

    const createTenant = async (plan = 'free') => {
        setLoading(true);
        try {
            // Always create tenant with 'free' plan first
            const response = await fetch(`${process.env.REACT_APP_API_URL}/tenants`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...getAuthHeaders()
                },
                body: JSON.stringify({ name, type, plan: 'free' })
            });

            if (response.ok) {
                const data = await response.json();
                setShowPlanModal(false);

                // Switch to new tenant first
                try {
                    await switchTenant(data._id);
                } catch (err) {
                    localStorage.setItem('activeTenantId', data._id);
                    window.location.reload();
                }

                // Seul le plan gratuit peut être créé maintenant
                // Les utilisateurs peuvent upgrader depuis les paramètres du tenant

                onSuccess();
                navigate('/messages');
            } else {
                const data = await response.json().catch(() => ({ message: 'Erreur serveur' }));

                // Vérifier si c'est une erreur de nom existant
                const isNameConflict = response.status === 400 &&
                    data.message &&
                    (data.message.includes('existe déjà') ||
                        data.message.includes('existe deja') ||
                        data.message.toLowerCase().includes('organisation existe'));

                if (isNameConflict) {
                    setConflictingName(name);
                    setShowPlanModal(false);
                    // Petit délai pour s'assurer que la modale de plan est fermée
                    setTimeout(() => setShowConflictModal(true), 150);
                } else {
                    toast.error(data.message || 'Erreur lors de la création');
                }
            }
        } catch (error) {
            console.error('Error creating tenant:', error);
            toast.error('Erreur réseau');
        } finally {
            setLoading(false);
        }
    };

    // Modal actions
    const modalActions = (
        <>
            <button
                ref={cancelRef}
                type="button"
                onClick={onClose}
                disabled={loading}
                className="flex-1 bg-gray-300 dark:bg-gray-700 text-gray-800 dark:text-gray-200 font-bold py-2 px-4 rounded hover:bg-gray-400 dark:hover:bg-gray-600"
            >
                Annuler
            </button>
            <button
                ref={confirmRef}
                type="submit"
                form="create-tenant-form"
                disabled={loading || name.trim() === '' || name.trim().length > 40}
                className="flex-1 bg-primary text-white dark:text-black font-bold py-2 px-4 rounded hover:bg-primary-hover"
            >
                {loading ? 'Création...' : 'Créer'}
            </button>
        </>
    );

    return (
        <>
            {!showPlanModal && (
                <Modal
                    open={true}
                    onClose={onClose}
                    title="Créer une organisation"
                    actions={modalActions}
                    initialFocusRef={nameInputRef}
                >
                    <form id="create-tenant-form" onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Nom de l'organisation
                            </label>
                            <input
                                ref={nameInputRef}
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className="w-full p-3 border dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded focus:ring-2 focus:ring-primary focus:border-primary"
                                required
                                disabled={loading}
                                placeholder="Mon entreprise"
                                maxLength={40}
                            />
                            <div className="text-xs text-gray-500 pt-1">{name.trim().length}/40</div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Type
                            </label>
                            <select
                                value={type}
                                onChange={(e) => setType(e.target.value)}
                                className="w-full p-3 border dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded focus:ring-2 focus:ring-primary focus:border-primary"
                                disabled={loading}
                            >
                                <option value="professional">Professionnel</option>
                                <option value="personal">Personnel</option>
                            </select>
                        </div>
                    </form>
                </Modal>
            )}
            <PricingModal
                open={showPlanModal}
                onClose={() => setShowPlanModal(false)}
                currentPlan={selectedPlan}
                tenantId={null}
                onSelect={(planId) => {
                    setSelectedPlan(planId);
                    createTenant(planId);
                }}
            />
            <TenantNameConflictModal
                open={showConflictModal}
                onClose={() => {
                    setShowConflictModal(false);
                    setConflictingName('');
                    onClose();
                }}
                existingName={conflictingName}
                onRetry={() => {
                    setShowConflictModal(false);
                    setConflictingName('');
                }}
            />
        </>
    );
};

export default CreateTenantModal;
